/**
 * confirmUnsaved — shows a styled modal asking the user to save or discard
 * unsaved changes. Returns 'save' | 'discard' | 'cancel'.
 *
 * Uses a native <dialog> element so it blocks interaction with the rest of
 * the page. Styled to match the editor's dark theme.
 */

export type UnsavedChoice = 'save' | 'discard' | 'cancel';

export function confirmUnsaved(message?: string): Promise<UnsavedChoice> {
  return new Promise((resolve) => {
    const dialog = document.createElement('dialog');
    dialog.style.cssText = [
      'border: none',
      'border-radius: 12px',
      'background: #2e2a25',
      'color: #f7e5c5',
      'font-family: Inter, sans-serif',
      'padding: 28px 32px 24px',
      'max-width: 380px',
      'box-shadow: 0 12px 40px rgba(0,0,0,0.5)',
    ].join(';');

    // Backdrop styling
    const style = document.createElement('style');
    style.textContent = `dialog::backdrop { background: rgba(0,0,0,0.45); }`;
    dialog.appendChild(style);

    const msg = document.createElement('p');
    msg.textContent = message ?? 'You have unsaved changes.';
    msg.style.cssText = 'margin: 0 0 20px; font-size: 14px; line-height: 1.5;';
    dialog.appendChild(msg);

    const btnRow = document.createElement('div');
    btnRow.style.cssText = 'display: flex; gap: 10px; justify-content: flex-end;';

    const btnBase = 'border: none; border-radius: 6px; font-family: Inter, sans-serif; font-size: 13px; font-weight: 600; padding: 8px 16px; cursor: pointer;';

    const discardBtn = document.createElement('button');
    discardBtn.textContent = 'Discard Edits';
    discardBtn.style.cssText = `${btnBase} background: #514b44; color: #f7e5c5;`;

    const saveBtn = document.createElement('button');
    saveBtn.textContent = 'Save';
    saveBtn.style.cssText = `${btnBase} background: #387c47; color: #f7e5c5;`;

    btnRow.appendChild(discardBtn);
    btnRow.appendChild(saveBtn);
    dialog.appendChild(btnRow);

    function cleanup(choice: UnsavedChoice) {
      dialog.close();
      dialog.remove();
      resolve(choice);
    }

    saveBtn.addEventListener('click', () => cleanup('save'));
    discardBtn.addEventListener('click', () => cleanup('discard'));
    // Pressing Escape or clicking backdrop = cancel (stay on current puzzle)
    dialog.addEventListener('cancel', (e) => {
      e.preventDefault();
      cleanup('cancel');
    });

    document.body.appendChild(dialog);
    dialog.showModal();
    saveBtn.focus();
  });
}
